import { CircleAlert, CircleCheck, Download, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { isApiError } from '../../api/client'
import {
  startErrorText,
  useJob,
  useStartProbe,
  useStartYoutube,
  useToolsInfo,
  type ProbeResult,
  type ProbeVideo,
  type YoutubeFormat,
  type YoutubeQuality,
  type YoutubeResult,
} from '../../api/tools'
import { downloadFile } from '../../lib/download'
import { formatBytes, formatDuration, parseLinks, youtubeId } from '../../lib/links'
import { useViewState } from '../../lib/navigation'
import { isInTelegram } from '../../telegram'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Select } from '../Select'
import { Spinner } from '../Spinner'
import { Choice } from './Choice'
import { JobProgress, JobResult } from './JobView'
import { LinksField } from './LinksField'

type Step = 'links' | 'params'

function fits(q: YoutubeQuality, cap: number): boolean {
  return q.size === null || q.size <= cap
}

function qualityName(q: YoutubeQuality): string {
  return q.fps && q.fps > 30 ? `${q.label}${q.fps}` : q.label
}

function sizeText(size: number | null): string {
  return size === null ? 'размер неизвестен' : `~${formatBytes(size)}`
}

/** Качество по умолчанию — самое высокое, что влезает в предел файла. */
function defaultHeight(video: ProbeVideo, cap: number): number | null {
  const list = video.qualities ?? []
  return (list.find((q) => fits(q, cap)) ?? list[list.length - 1])?.height ?? null
}

export function YoutubeDialog({ stateKey, initial = '', onClose: finishClose }: { stateKey: string; initial?: string; onClose: () => void }) {
  const info = useToolsInfo()
  const probe = useStartProbe()
  const download = useStartYoutube()
  const [text, setText] = useViewState(`${stateKey}.text`, initial)
  const [step, setStep] = useViewState<Step>(`${stateKey}.step`, 'links')
  const [probeId, setProbeId] = useViewState<string | null>(`${stateKey}.probe`, null)
  const [jobId, setJobId] = useViewState<string | null>(`${stateKey}.job`, null)
  const [format, setFormat] = useViewState<YoutubeFormat>(`${stateKey}.format`, 'mp4')
  const [heights, setHeights] = useViewState<Record<string, number | null>>(`${stateKey}.heights`, {})
  const probeJob = useJob(probeId)
  const job = useJob(jobId)
  const [downloadedJob, setDownloadedJob] = useViewState<string | null>(`${stateKey}.downloaded`, null)
  const downloadedNow = useRef(downloadedJob)
  const onClose = () => {
    setText(''); setStep('links'); setProbeId(null); setJobId(null); setFormat('mp4'); setHeights({}); setDownloadedJob(null)
    finishClose()
  }

  const toChat = isInTelegram() && info.data?.telegram === true
  const max = info.data?.youtube_max_urls ?? 10
  const parsed = useMemo(() => parseLinks(text, youtubeId), [text])
  const ready = parsed.ids.length > 0 && parsed.bad.length === 0 && parsed.ids.length <= max

  const probed = probeJob.data
  const result = probed?.status === 'done' ? (probed.result as ProbeResult) : null
  const cap = result?.max_file_bytes ?? info.data?.max_file_bytes ?? 2 * 1024 ** 3
  const videos = useMemo(() => result?.videos ?? [], [result])
  const usable = videos.filter((v) => !v.error && (v.qualities?.length ?? 0) > 0)

  useEffect(() => {
    if (videos.length === 0) return
    setHeights((prev) => {
      const next = { ...prev }
      for (const v of videos) if (!(v.url in next)) next[v.url] = defaultHeight(v, cap)
      return next
    })
  }, [videos, cap])

  // Один файл в браузер — сразу на устройство.
  const data = job.data
  useEffect(() => {
    if (!data || data.status !== 'done' || downloadedJob === data.id || downloadedNow.current === data.id) return
    const files = (data.result as YoutubeResult).files.filter((f) => f.download_url && !f.sent)
    if (files.length !== 1) return
    downloadedNow.current = data.id
    setDownloadedJob(data.id)
    downloadFile(files[0]?.download_url ?? '', files[0]?.name ?? undefined)
  }, [data, downloadedJob, setDownloadedJob])

  const check = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || probe.isPending) return
    setStep('params')
    probe.mutate({ urls: parsed.lines }, { onSuccess: (created) => setProbeId(created.id), onError: () => setStep('links') })
  }

  const start = () => {
    if (usable.length === 0 || download.isPending) return
    const items = usable.map((v) => {
      const height = heights[v.url] ?? null
      const q = v.qualities?.find((x) => x.height === height) ?? null
      return {
        url: v.url,
        height: format === 'mp4' ? height : null,
        title: v.title ?? null,
        size: format === 'mp4' ? (q?.size ?? null) : (v.mp3_size ?? null),
      }
    })
    download.mutate(
      { items, format, deliver: toChat ? 'telegram' : 'browser' },
      { onSuccess: (created) => setJobId(created.id) },
    )
  }

  const back = () => {
    setStep('links')
    setProbeId(null)
    setHeights({})
  }

  const tooBig = usable.some((v) => {
    if (format === 'mp3') return (v.mp3_size ?? 0) > cap
    const q = v.qualities?.find((x) => x.height === heights[v.url])
    return q ? !fits(q, cap) : false
  })

  if (jobId) {
    const finished = data?.status === 'done' || data?.status === 'failed'
    return (
      <Dialog
        title="Скачать с YouTube"
        onClose={onClose}
        wide
        footer={
          <Button variant={finished ? 'primary' : 'ghost'} onClick={onClose}>
            {finished ? 'Готово' : 'Свернуть'}
          </Button>
        }
      >
        {!data ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : finished ? (
          <JobResult job={data} />
        ) : (
          <JobProgress job={data} />
        )}
      </Dialog>
    )
  }

  if (step === 'params') {
    const checking = !probed || probed.status === 'queued' || probed.status === 'running'
    return (
      <Dialog
        title="Параметры скачивания"
        onClose={onClose}
        wide
        footer={
          <>
            <Button variant="ghost" onClick={back}>
              Назад
            </Button>
            <Button
              variant="primary"
              onClick={start}
              disabled={checking || usable.length === 0 || tooBig || download.isPending}
            >
              {download.isPending || checking ? (
                <Spinner size={14} className="text-accent-fg" />
              ) : toChat ? (
                <Send size={14} />
              ) : (
                <Download size={14} />
              )}
              {checking ? 'Проверяем видео...' : toChat ? 'Отправить в чат' : 'Скачать'}
            </Button>
          </>
        }
      >
        {checking ? (
          probed ? (
            <JobProgress job={probed} />
          ) : (
            <div className="flex items-center gap-3 py-6 text-sm text-muted">
              <Spinner size={18} />
              Проверяем видео...
            </div>
          )
        ) : probed.status === 'failed' ? (
          <JobResult job={probed} />
        ) : (
          <div className="space-y-4">
            <ul className="space-y-3">
              {videos.map((v) => (
                <VideoRow
                  key={v.url}
                  video={v}
                  format={format}
                  cap={cap}
                  height={heights[v.url] ?? null}
                  onHeight={(h) => setHeights((prev) => ({ ...prev, [v.url]: h }))}
                />
              ))}
            </ul>
            <fieldset>
              <legend className="mb-1 text-xs text-muted">Формат</legend>
              <div className="flex flex-wrap gap-x-5">
                <Choice
                  type="radio"
                  name="yt-format"
                  label="MP4 — видео"
                  checked={format === 'mp4'}
                  onChange={() => setFormat('mp4')}
                />
                <Choice
                  type="radio"
                  name="yt-format"
                  label="MP3 — только аудио"
                  checked={format === 'mp3'}
                  onChange={() => setFormat('mp3')}
                />
              </div>
            </fieldset>
            <p className="text-xs text-pretty text-muted">
              {toChat ? 'Файлы придут в чат с ботом. ' : ''}
              Файл до {formatBytes(cap)}; скачивание идёт по одному видео за раз.
            </p>
            {tooBig && <p className="text-xs text-danger">Есть файл больше {formatBytes(cap)}: выберите качество ниже.</p>}
            {download.isError && (
              <p className="text-sm text-danger">
                {startErrorText(isApiError(download.error) ? download.error.detail : null)}
              </p>
            )}
          </div>
        )}
      </Dialog>
    )
  }

  return (
    <Dialog
      title="Скачать с YouTube"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="tools-youtube" disabled={!ready || probe.isPending}>
            {probe.isPending && <Spinner size={14} className="text-accent-fg" />}
            Продолжить
          </Button>
        </>
      }
    >
      <form id="tools-youtube" onSubmit={check} className="space-y-4">
        <p className="text-sm text-pretty text-muted">
          Вставьте ссылки на видео с YouTube — по одной на строку. Мы проверим доступные качества и{' '}
          {toChat ? 'отправим файлы в чат с ботом.' : 'сохраним файлы на ваше устройство.'}
        </p>
        <LinksField
          label="Ссылки на видео"
          value={text}
          onChange={setText}
          parsed={parsed}
          placeholder="https://www.youtube.com/watch?v=…"
          hint={`По одной ссылке на строку, до ${max} видео`}
          max={max}
          kindLabel="видео YouTube"
        />
        {probe.isError && (
          <p className="text-sm text-danger">{startErrorText(isApiError(probe.error) ? probe.error.detail : null)}</p>
        )}
      </form>
    </Dialog>
  )
}

function VideoRow({
  video,
  format,
  cap,
  height,
  onHeight,
}: {
  video: ProbeVideo
  format: YoutubeFormat
  cap: number
  height: number | null
  onHeight: (h: number) => void
}) {
  if (video.error) {
    return (
      <li className="flex items-start gap-2.5 rounded-xl bg-surface p-3 ring-1 ring-line ring-inset">
        <CircleAlert size={18} className="mt-0.5 shrink-0 text-danger" />
        <div className="min-w-0">
          <p className="truncate text-sm">{video.url}</p>
          <p className="text-xs text-danger">{video.error}</p>
        </div>
      </li>
    )
  }
  const list = video.qualities ?? []
  const top = list[0]
  const meta = [top && qualityName(top), top && sizeText(top.size), formatDuration(video.duration)]
    .filter(Boolean)
    .join(' · ')
  return (
    <li className="rounded-xl bg-surface p-3 ring-1 ring-line ring-inset">
      <div className="flex items-start gap-2.5">
        <CircleCheck size={18} className="mt-0.5 shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-pretty">{video.title}</p>
          <p className="text-xs text-muted tabular-nums">{meta}</p>
        </div>
      </div>
      <div className="mt-2.5 pl-7">
        {format === 'mp3' ? (
          <p className="text-xs text-muted">MP3 · {sizeText(video.mp3_size ?? null)}</p>
        ) : (
          <Select
            label="Качество"
            className="w-full"
            value={height ?? ''}
            onChange={(e) => onHeight(Number(e.target.value))}
          >
            {list.map((q, i) => {
              const name = i === 0 ? `Максимальное (${qualityName(q)})` : qualityName(q)
              const over = !fits(q, cap)
              return (
                <option key={`${q.height}-${q.label}`} value={q.height} disabled={over}>
                  {name} · {sizeText(q.size)}
                  {over ? ` — больше ${formatBytes(cap)}` : ''}
                </option>
              )
            })}
          </Select>
        )}
      </div>
    </li>
  )
}
